import type { ResultOf } from "gql.tada";
import { graphql } from "@/data-access/graphql";

/**
 * Fetch paginated conversation list for the sidebar.
 * Uses OffsetPaginated — returns results + totalCount.
 * Ordering applied server-side (default: -updated_at from the resolver).
 */
export const GetConversations = graphql(`
  query getConversations($pagination: OffsetPaginationInput = { limit: 30 }) {
    conversations(pagination: $pagination) {
      results {
        id
        title
        messageCount
        createdAt
        updatedAt
      }
      totalCount
    }
  }
`);

export type HeraldPreviousConversation = ResultOf<
  typeof GetConversations
>["conversations"]["results"][number];

/**
 * Search conversations by title. Partial match is server-side (icontains-style).
 * `searchMatch` is populated only when filtering by `title` (matched words wrapped in `<mark>`).
 */
export const SearchConversations = graphql(`
  query searchConversations(
    $filters: ConversationFilter
    $pagination: OffsetPaginationInput = { limit: 30, offset: 0 }
  ) {
    conversations(filters: $filters, pagination: $pagination) {
      results {
        id
        title
        searchMatch
        updatedAt
      }
      totalCount
    }
  }
`);

export type ChatSearchConversation = ResultOf<
  typeof SearchConversations
>["conversations"]["results"][number];

/**
 * Rename a chat conversation. Success returns conversation fields; errors return `OperationInfo.messages`.
 */
export const RenameConversation = graphql(`
  mutation renameConversation($id: ID!, $title: String!) {
    renameConversation(id: $id, title: $title) {
      ... on ConversationType {
        __typename
        id
        title
        messageCount
        createdAt
        updatedAt
      }
      ... on OperationInfo {
        __typename
        messages {
          kind
          message
          code
        }
      }
    }
  }
`);

/**
 * Delete a chat conversation by id. Success returns `SuccessResponse`; errors return `OperationInfo.messages`.
 */
export const DeleteConversation = graphql(`
  mutation deleteConversation($id: ID!) {
    deleteConversation(id: $id) {
      ... on SuccessResponse {
        __typename
        message
      }
      ... on OperationInfo {
        __typename
        messages {
          kind
          message
          code
        }
      }
    }
  }
`);

/**
 * Load messages for a specific conversation.
 * Uses OffsetPaginated with MessageFilter to scope by conversation_id.
 * Ordering applied server-side (default: sequence ASC from the resolver).
 */
export const GetConversationMessages = graphql(`
  query getConversationMessages(
    $filters: MessageFilter!
    $pagination: OffsetPaginationInput = { limit: 100 }
  ) {
    conversationMessages(filters: $filters, pagination: $pagination) {
      results {
        id
        role
        content
        parts
        sequence
        status
        createdAt
      }
      totalCount
    }
  }
`);
